import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AuthorityState, SourceAuthorityMetadata } from '@agentwiki/shared'
import { apiClient, ApiError } from '../lib/api-client'

export interface SourceAuthorityUpdate {
  state: AuthorityState
  expiresAt?: number | null
  expectedReviewedAt: number | null
}

export type SourceAuthorityUpdateResult = SourceAuthorityMetadata & { changed?: boolean }

export function sourceAuthorityErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.status === 403) {
    return 'Authority review is unavailable for your current role.'
  }
  if (error instanceof ApiError && error.status === 404) {
    return 'Authority review is unavailable for this document.'
  }
  if (error instanceof ApiError && error.status === 409) {
    return 'Authority changed in another review. Latest state has been reloaded.'
  }
  return 'AgentWiki could not load or save this authority review. No authority state was changed.'
}

export function useSourceAuthority(
  sourceId: string,
  enabled: boolean,
  initialData?: SourceAuthorityMetadata,
) {
  return useQuery<SourceAuthorityMetadata, ApiError>({
    queryKey: ['source-authority', sourceId],
    queryFn: () =>
      apiClient.get<SourceAuthorityMetadata>(`/api/sync/sources/${sourceId}/authority`),
    enabled: enabled && Boolean(sourceId),
    initialData,
    retry: (count, error) => error.status !== 403 && error.status !== 404 && count < 1,
  })
}

export function useUpdateSourceAuthority(sourceId: string, documentId: string) {
  const queryClient = useQueryClient()
  return useMutation<SourceAuthorityUpdateResult, ApiError, SourceAuthorityUpdate>({
    mutationFn: (input) =>
      apiClient.patch<SourceAuthorityUpdateResult>(
        `/api/sync/sources/${sourceId}/authority`,
        input,
      ),
    onSuccess: async (authority) => {
      queryClient.setQueryData(['source-authority', sourceId], authority)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['documents'] }),
        queryClient.invalidateQueries({ queryKey: ['documents', documentId] }),
      ])
    },
    onError: async (error) => {
      if (error.status !== 409) return
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['source-authority', sourceId] }),
        queryClient.invalidateQueries({ queryKey: ['documents'] }),
      ])
    },
  })
}
